import { useEffect, useState } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
  color: 'purple' | 'pink' | 'blue';
}

interface FloatingOrb {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
}

export function PricingBackground() {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [orbs, setOrbs] = useState<FloatingOrb[]>([]);

  useEffect(() => {
    // Generate floating particles
    const newParticles: Particle[] = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 6 + 2,
      speed: Math.random() * 20 + 10,
      opacity: Math.random() * 0.5 + 0.2,
      color: ['purple', 'pink', 'blue'][Math.floor(Math.random() * 3)] as 'purple' | 'pink' | 'blue',
    }));
    setParticles(newParticles);

    // Generate large floating orbs
    const newOrbs: FloatingOrb[] = [
      { id: 0, x: 20, y: 30, size: 300, delay: 0, duration: 8 },
      { id: 1, x: 80, y: 20, size: 250, delay: 2, duration: 10 },
      { id: 2, x: 60, y: 70, size: 350, delay: 4, duration: 12 },
      { id: 3, x: 10, y: 80, size: 200, delay: 1, duration: 9 },
      { id: 4, x: 90, y: 60, size: 280, delay: 3, duration: 11 },
    ];
    setOrbs(newOrbs);
  }, []);

  const getParticleColor = (color: string) => {
    switch (color) {
      case 'purple':
        return 'rgba(139, 92, 246, VAR)';
      case 'pink':
        return 'rgba(236, 72, 153, VAR)';
      case 'blue':
        return 'rgba(59, 130, 246, VAR)';
      default:
        return 'rgba(139, 92, 246, VAR)';
    }
  };

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Base gradient mesh */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-purple-500/5" />
        <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/3 via-transparent to-blue-500/3" />
      </div>

      {/* Large floating orbs with blur */}
      {orbs.map((orb) => (
        <div
          key={orb.id}
          className="absolute rounded-full"
          style={{
            left: `${orb.x}%`,
            top: `${orb.y}%`,
            width: orb.size,
            height: orb.size,
            background: `radial-gradient(circle, ${
              orb.id % 3 === 0 ? 'rgba(139, 92, 246, 0.15)' :
              orb.id % 3 === 1 ? 'rgba(236, 72, 153, 0.1)' :
              'rgba(59, 130, 246, 0.12)'
            } 0%, transparent 70%)`,
            filter: 'blur(40px)',
            animation: `orbFloat ${orb.duration}s ease-in-out infinite ${orb.delay}s`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}

      {/* Animated grid lines */}
      <svg className="absolute inset-0 w-full h-full opacity-10">
        <defs>
          <pattern id="pricingGrid" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(139, 92, 246, 0.3)" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#pricingGrid)" />
      </svg>

      {/* Floating particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            backgroundColor: getParticleColor(particle.color).replace('VAR', String(particle.opacity)),
            boxShadow: `0 0 ${particle.size * 2}px ${getParticleColor(particle.color).replace('VAR', String(particle.opacity * 0.5))}`,
            animation: `particleRise ${particle.speed}s linear infinite`,
          }}
        />
      ))}

      {/* Sparkle effects */}
      {[...Array(8)].map((_, i) => (
        <div
          key={`sparkle-${i}`}
          className="absolute"
          style={{
            left: `${15 + i * 10}%`,
            top: `${20 + (i % 3) * 25}%`,
            animation: `sparkle 3s ease-in-out infinite ${i * 0.4}s`,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path
              d="M10 0L11.5 8.5L20 10L11.5 11.5L10 20L8.5 11.5L0 10L8.5 8.5L10 0Z"
              fill="rgba(139, 92, 246, 0.6)"
            />
          </svg>
        </div>
      ))}


      {/* Corner glows - subtle radial gradients */}
      <div
        className="absolute top-0 left-0 w-1/2 h-1/2"
        style={{
          background: 'radial-gradient(circle at top left, rgba(139, 92, 246, 0.08) 0%, transparent 50%)',
        }}
      />
      <div
        className="absolute bottom-0 right-0 w-1/2 h-1/2"
        style={{
          background: 'radial-gradient(circle at bottom right, rgba(139, 92, 246, 0.05) 0%, transparent 50%)',
        }}
      />

      {/* Animation keyframes */}
      <style>{`
        @keyframes orbFloat {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.5;
          }
          50% {
            transform: translate(-50%, -50%) translate(30px, -20px) scale(1.1);
            opacity: 0.8;
          }
        }

        @keyframes particleRise {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100vh) translateX(20px);
            opacity: 0;
          }
        }

        @keyframes sparkle {
          0%, 100% {
            transform: scale(0) rotate(0deg);
            opacity: 0;
          }
          50% {
            transform: scale(1) rotate(180deg);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
